import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { FileText, Plus, Search, Calendar, User, Building, CheckCircle, XCircle, RefreshCw, X, Save, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Edit, Mail } from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import Select from 'react-select';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ServiceWorkOrder = () => {
  const { user } = useContext(UserContext);

  // Tab state
  const [activeTab, setActiveTab] = useState('issued');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter states - arrays for multi-select
  const [signedFilter, setSignedFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState([]);

  // Dropdown visibility states
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showCreatedByDropdown, setShowCreatedByDropdown] = useState(false);

  // Search queries for dropdowns
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [createdBySearchQuery, setCreatedBySearchQuery] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);

  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Filter data
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterProjects, setFilterProjects] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null); // null = new, object = edit mode
  const [modalLoading, setModalLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [globalRoles, setGlobalRoles] = useState([]);
  const [userGlobalRoles, setUserGlobalRoles] = useState([]);
  const [userRoles, setUserRoles] = useState({}); // { userId: { role: 'Site Manager', roles: [...] } }
  const [formData, setFormData] = useState({
    user: '',
    project: '',
    type_service_work: '',
    date_start: '',
    date_end: '',
    required_reports: '',
    number_of_turbines: '',
    turbine_type: ''
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [swoTypeCheckboxes, setSwoTypeCheckboxes] = useState({ admin: false, instalation: false }); // For unknown role case

  // Role to SWO type mapping: 'admin' = type_swo_admin, 'instalation' = type_swo_instalation
  const ROLE_TYPE_MAPPING = {
    '#LVL C': 'instalation',
    'Biuro': 'admin',
    'CORE': 'instalation',
    'HSE': 'admin',
    'SE': 'admin',
    'SM': 'admin',
    'STCK': 'instalation',
    'SUPP': 'instalation',
    'SV': 'admin',
    'TL': 'instalation',
  };

  // Helper function to get SWO type for a role
  const getRoleType = (roleName) => {
    // First try exact match
    if (ROLE_TYPE_MAPPING[roleName]) {
      return ROLE_TYPE_MAPPING[roleName];
    }
    // Then try case-insensitive match
    const upperRole = roleName.toUpperCase();
    for (const [key, value] of Object.entries(ROLE_TYPE_MAPPING)) {
      if (key.toUpperCase() === upperRole) {
        return value;
      }
    }
    return null; // Unknown role
  };

  // Determine SWO type text based on user roles and project
  const getTypeServiceWorkText = (userId, projectId) => {
    const userRole = userRoles[userId];
    const project = projects.find(p => p.id === projectId);

    if (!userRole?.roles?.length || !project) {
      return { text: '', hasInstallation: false, hasAdmin: false, unknownRole: !userRole?.roles?.length && userId };
    }

    const roles = userRole.roles;
    let hasInstallationRole = false;
    let hasAdminRole = false;
    let hasUnknownRole = false;

    for (const role of roles) {
      const roleType = getRoleType(role);
      if (roleType === 'instalation') {
        hasInstallationRole = true;
      } else if (roleType === 'admin') {
        hasAdminRole = true;
      } else {
        hasUnknownRole = true;
      }
    }

    // If all roles are unknown, show checkboxes
    if (!hasInstallationRole && !hasAdminRole && hasUnknownRole) {
      return { text: '', hasInstallation: false, hasAdmin: false, unknownRole: true };
    }

    const texts = [];
    if (hasInstallationRole && project.type_swo_instalation) {
      texts.push(project.type_swo_instalation);
    }
    if (hasAdminRole && project.type_swo_admin) {
      texts.push(project.type_swo_admin);
    }

    return {
      text: texts.join('\n\n'),
      hasInstallation: hasInstallationRole,
      hasAdmin: hasAdminRole,
      unknownRole: false
    };
  };

  // Update type_service_work when user or project changes
  const updateTypeServiceWork = (userId, projectId, currentFormData) => {
    const result = getTypeServiceWorkText(userId, projectId);

    if (result.unknownRole) {
      // Don't auto-fill if unknown role - user will use checkboxes
      setSwoTypeCheckboxes({ admin: false, instalation: false });
      return { ...currentFormData, type_service_work: '' };
    }

    setSwoTypeCheckboxes({ admin: false, instalation: false });
    return { ...currentFormData, type_service_work: result.text };
  };

  // Planning tab state
  const [planningData, setPlanningData] = useState([]);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [planningError, setPlanningError] = useState(null);
  const [allOrders, setAllOrders] = useState([]); // All CPOs for coverage comparison

  // Planning tab sorting state
  const [planningSortField, setPlanningSortField] = useState('project');
  const [planningSortDirection, setPlanningSortDirection] = useState('asc');

  // Planning tab selection state for bulk operations
  const [selectedPlanningRows, setSelectedPlanningRows] = useState(new Set());
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { created, extended, skipped, errors }

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // order to delete
  const [deleting, setDeleting] = useState(false);

  // Email sending state
  const [emailConfirm, setEmailConfirm] = useState(null); // order to send email for
  const [sendingEmail, setSendingEmail] = useState(false);

  // React-select custom styles
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
      borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
      '&:hover': {
        borderColor: '#d1d5db',
      },
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1f2937',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : '#fff',
      color: state.isSelected ? '#fff' : '#1f2937',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#dbeafe',
      },
    }),
  };

  // Create options for react-select with roles
  const userOptions = users.map((u) => {
    const userRole = userRoles[u.id];
    const roleText = userRole?.roles?.length > 0 ? ` [${userRole.roles.join(', ')}]` : '';
    return {
      value: u.id,
      label: `${u.first_name} ${u.last_name}${roleText}`.trim(),
      role: userRole?.role || null,
      roles: userRole?.roles || []
    };
  });

  const projectOptions = projects
    .filter((p) => p.is_active !== false)
    .map((p) => ({
      value: p.id,
      label: p.name
    }));

  // Get auth headers
  const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch filter data (users and projects)
  const fetchFilterData = async () => {
    try {
      const [usersRes, projectsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/users/minimal/`, { headers: getHeaders() }),
        fetch(`${API_BASE_URL}/api/projects/`, { headers: getHeaders() })
      ]);

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setFilterUsers(Array.isArray(usersData) ? usersData : (usersData.results || []));
      }

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setFilterProjects(Array.isArray(projectsData) ? projectsData : (projectsData.results || []));
      }
    } catch (err) {
      console.error('Error fetching filter data:', err);
    }
  };

  // Fetch construction project orders
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);

      if (signedFilter) params.append('signed', signedFilter);
      // Multi-select filters - send as comma-separated values
      if (selectedUsers.length > 0) params.append('users', selectedUsers.join(','));
      if (selectedProjects.length > 0) params.append('projects', selectedProjects.join(','));
      if (selectedCreatedBy.length > 0) params.append('created_by_ids', selectedCreatedBy.join(','));
      if (searchTerm) params.append('search', searchTerm);

      // Sorting
      if (sortField) {
        const ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
        params.append('ordering', ordering);
      }

      const url = `${API_BASE_URL}/api/service-work-orders/?${params}`;
      const response = await fetch(url, { headers: getHeaders() });

      if (!response.ok) {
        throw new Error(`Failed to fetch construction project orders (${response.status})`);
      }

      const data = await response.json();
      if (data.results) {
        setOrders(data.results);
        setTotalCount(data.count || 0);
      } else {
        setOrders(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, signedFilter, selectedUsers, selectedProjects, selectedCreatedBy, searchTerm, sortField, sortDirection]);

  // Fetch planning data (resource assignments from today onwards)
  const fetchPlanningData = useCallback(async () => {
    setPlanningLoading(true);
    setPlanningError(null);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [planningRes, globalRolesRes, userGlobalRolesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/resource-planning-assignments/?end_date_from=${today}&exclude_special=true`, { headers: getHeaders() }),
        fetch(`${API_BASE_URL}/api/global-roles/`, { headers: getHeaders() }),
        fetch(`${API_BASE_URL}/api/user-global-roles/`, { headers: getHeaders() })
      ]);

      if (!planningRes.ok) {
        throw new Error(`Failed to fetch planning data (${planningRes.status})`);
      }

      const data = await planningRes.json();
      setPlanningData(Array.isArray(data) ? data : (data.results || []));

      // Process roles
      let fetchedGlobalRoles = [];
      let fetchedUserGlobalRoles = [];

      if (globalRolesRes.ok) {
        const globalRolesData = await globalRolesRes.json();
        fetchedGlobalRoles = Array.isArray(globalRolesData) ? globalRolesData : (globalRolesData.results || []);
        setGlobalRoles(fetchedGlobalRoles);
      }

      if (userGlobalRolesRes.ok) {
        const userGlobalRolesData = await userGlobalRolesRes.json();
        fetchedUserGlobalRoles = Array.isArray(userGlobalRolesData) ? userGlobalRolesData : (userGlobalRolesData.results || []);
        setUserGlobalRoles(fetchedUserGlobalRoles);
      }

      // Build userRoles map
      const userRolesMap = {};
      for (const userRole of fetchedUserGlobalRoles) {
        const globalRole = fetchedGlobalRoles.find(r => r.id === userRole.role);
        if (globalRole) {
          const odId = userRole.user || userRole.user_id;
          if (!userRolesMap[odId]) {
            userRolesMap[odId] = { roles: [], colors: [] };
          }
          userRolesMap[odId].roles.push(globalRole.name);
          userRolesMap[odId].colors.push(globalRole.color);
          if (!userRolesMap[odId].role) {
            userRolesMap[odId].role = globalRole.name;
            userRolesMap[odId].color = globalRole.color;
          }
        }
      }
      setUserRoles(prev => ({ ...prev, ...userRolesMap }));

    } catch (err) {
      setPlanningError(err.message);
      console.error('Error fetching planning data:', err);
    } finally {
      setPlanningLoading(false);
    }
  }, []);

  // Fetch all current/future CPOs for coverage comparison
  const fetchAllCurrentOrders = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const url = `${API_BASE_URL}/api/service-work-orders/?date_end_from=${today}&page_size=1000`;
      const response = await fetch(url, { headers: getHeaders() });

      if (response.ok) {
        const data = await response.json();
        setAllOrders(Array.isArray(data) ? data : (data.results || []));
      }
    } catch (err) {
      console.error('Error fetching all orders:', err);
    }
  }, []);

  // Helper function to add days to a date string
  const addDays = (dateStr, days) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  // Aggregate planning data - group by user + project, merge date ranges, calculate CPO gaps
  const aggregatedPlanningData = useMemo(() => {
    if (!planningData.length) return [];

    const today = new Date().toISOString().split('T')[0];

    // Group by user_id + project_id
    const grouped = {};

    planningData.forEach(assignment => {
      const key = `${assignment.user}-${assignment.project}`;

      if (!grouped[key]) {
        grouped[key] = {
          user_id: assignment.user,
          project_id: assignment.project,
          user_data: assignment.user_data,
          project_data: assignment.project_data,
          start_date: assignment.start_date,
          end_date: assignment.end_date,
          roles: new Set(assignment.role ? [assignment.role] : []),
          assignments: [assignment]
        };
      } else {
        // Expand date range
        if (assignment.start_date < grouped[key].start_date) {
          grouped[key].start_date = assignment.start_date;
        }
        if (assignment.end_date > grouped[key].end_date) {
          grouped[key].end_date = assignment.end_date;
        }
        // Collect roles
        if (assignment.role) {
          grouped[key].roles.add(assignment.role);
        }
        grouped[key].assignments.push(assignment);
      }
    });

    // Process each group - calculate CPO coverage gaps
    const result = [];

    Object.values(grouped).forEach(item => {
      // Adjust start_date if in the past
      let needStart = item.start_date < today ? today : item.start_date;
      const needEnd = item.end_date;

      // Find all CPOs for this user + project
      const relevantCPOs = allOrders.filter(
        cpo => cpo.user === item.user_id && cpo.project === item.project_id
      ).sort((a, b) => a.date_start.localeCompare(b.date_start));

      // Calculate coverage - find the latest continuous coverage from needStart
      let coveredUntil = null;
      let lastCoveringCPO = null; // Track the CPO that provides the coverage

      for (const cpo of relevantCPOs) {
        const cpoStart = cpo.date_start < today ? today : cpo.date_start;
        const cpoEnd = cpo.date_end;

        // Check if this CPO overlaps with or extends from our needed range
        if (cpoStart <= needStart && cpoEnd >= needStart) {
          // CPO covers our start, extend coverage
          coveredUntil = cpoEnd;
          lastCoveringCPO = cpo;
        } else if (coveredUntil && cpoStart <= addDays(coveredUntil, 1) && cpoEnd > coveredUntil) {
          // CPO continues from previous coverage (with max 1 day gap)
          coveredUntil = cpoEnd;
          lastCoveringCPO = cpo;
        }
      }

      // Determine the gap
      let gapStart, gapEnd;

      if (!coveredUntil) {
        // No coverage at all
        gapStart = needStart;
        gapEnd = needEnd;
      } else if (coveredUntil >= needEnd) {
        // Fully covered - skip this item
        return;
      } else {
        // Partially covered - gap starts after coverage ends
        gapStart = addDays(coveredUntil, 1);
        gapEnd = needEnd;
      }

      // Only add if there's actually a gap
      if (gapStart <= gapEnd) {
        // Check if we can extend an existing unsigned CPO
        const canExtendCPO = lastCoveringCPO && !lastCoveringCPO.is_signed;

        result.push({
          ...item,
          start_date: gapStart,
          end_date: gapEnd,
          original_start: item.start_date,
          original_end: item.end_date,
          roles: Array.from(item.roles).join(', ') || '-',
          assignment_count: item.assignments.length,
          has_partial_coverage: !!coveredUntil,
          covered_until: coveredUntil,
          // For extending existing CPO
          can_extend_cpo: canExtendCPO,
          existing_cpo: canExtendCPO ? lastCoveringCPO : null
        });
      }
    });

    // Sort based on planningSortField and planningSortDirection
    return result.sort((a, b) => {
      let compareValue = 0;

      switch (planningSortField) {
        case 'user':
          const userA = `${a.user_data?.last_name || ''} ${a.user_data?.first_name || ''}`.trim().toLowerCase();
          const userB = `${b.user_data?.last_name || ''} ${b.user_data?.first_name || ''}`.trim().toLowerCase();
          compareValue = userA.localeCompare(userB);
          break;
        case 'project':
          compareValue = (a.project_data?.name || '').toLowerCase().localeCompare((b.project_data?.name || '').toLowerCase());
          break;
        case 'role':
          compareValue = (a.roles || '').toLowerCase().localeCompare((b.roles || '').toLowerCase());
          break;
        case 'period':
          compareValue = a.start_date.localeCompare(b.start_date);
          break;
        case 'status':
          // Sort by status: No CPO first, then Partial, then Can extend
          const getStatusOrder = (item) => {
            if (!item.has_partial_coverage) return 0; // No CPO
            if (!item.can_extend_cpo) return 1; // Partial (signed)
            return 2; // Can extend
          };
          compareValue = getStatusOrder(a) - getStatusOrder(b);
          break;
        default:
          compareValue = (a.project_data?.name || '').localeCompare(b.project_data?.name || '');
      }

      return planningSortDirection === 'asc' ? compareValue : -compareValue;
    });
  }, [planningData, allOrders, planningSortField, planningSortDirection]);

  // Toggle handlers for multi-select filters
  const handleUserToggle = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleCreatedByToggle = (userId) => {
    setSelectedCreatedBy(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  // Handle Planning tab sorting
  const handlePlanningSort = (field) => {
    if (planningSortField === field) {
      setPlanningSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setPlanningSortField(field);
      setPlanningSortDirection('asc');
    }
  };

  // Planning row selection helpers
  const getPlanningRowKey = (item) => `${item.user_id}-${item.project_id}`;

  const handleSelectPlanningRow = (item) => {
    const key = getPlanningRowKey(item);
    setSelectedPlanningRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleSelectAllPlanning = () => {
    if (selectedPlanningRows.size === aggregatedPlanningData.length && aggregatedPlanningData.length > 0) {
      // All selected - deselect all
      setSelectedPlanningRows(new Set());
    } else {
      // Select all rows
      const allKeys = new Set(aggregatedPlanningData.map(item => getPlanningRowKey(item)));
      setSelectedPlanningRows(allKeys);
    }
  };

  // Bulk create/extend CPO for selected rows
  const handleBulkCreateCPO = async () => {
    const selectedItems = aggregatedPlanningData.filter(
      item => selectedPlanningRows.has(getPlanningRowKey(item))
    );

    if (selectedItems.length === 0) return;

    setBulkCreating(true);

    // Fetch full projects data if not already loaded (needed for type_swo_* fields)
    let projectsData = projects;
    if (projects.length === 0) {
      try {
        const projectsRes = await fetch(`${API_BASE_URL}/api/projects/`, { headers: getHeaders() });
        if (projectsRes.ok) {
          const data = await projectsRes.json();
          projectsData = Array.isArray(data) ? data : (data.results || []);
          setProjects(projectsData);
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
      }
    }

    let createdCount = 0;
    let extendedCount = 0;
    const skippedItems = [];
    const errorItems = [];

    for (const item of selectedItems) {
      try {
        // Get full project data
        const fullProject = projectsData.find(p => p.id === item.project_id);
        const userName = `${item.user_data?.first_name || ''} ${item.user_data?.last_name || ''}`.trim();
        const projectName = item.project_data?.name || fullProject?.name || `Project ${item.project_id}`;

        // Calculate end date: project end_date + 90 days
        let endDate = item.end_date;
        if (fullProject?.end_date) {
          const projectEndDate = new Date(fullProject.end_date);
          projectEndDate.setDate(projectEndDate.getDate() + 90);
          endDate = projectEndDate.toISOString().split('T')[0];
        }

        if (item.can_extend_cpo && item.existing_cpo) {
          // EXTEND existing CPO (PUT)
          const cpo = item.existing_cpo;
          const payload = {
            user: cpo.user,
            project: cpo.project,
            type_service_work: cpo.type_service_work || '',
            date_start: cpo.date_start,
            date_end: endDate,
            required_reports: cpo.required_reports || '',
            number_of_turbines: cpo.number_of_turbines || '',
            turbine_type: cpo.turbine_type || ''
          };

          const response = await fetch(`${API_BASE_URL}/api/service-work-orders/${cpo.id}/`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            extendedCount++;
          } else {
            errorItems.push({ userName, projectName, reason: 'API error while extending' });
            console.error(`Failed to extend CPO ${cpo.id}`);
          }
        } else {
          // CREATE new CPO (POST)
          const userId = item.user_id;
          const userRole = userRoles[userId];
          let typeServiceWork = '';
          let skipReason = '';

          if (!userRole?.roles?.length) {
            skipReason = 'User has no role assigned';
          } else if (!fullProject) {
            skipReason = 'Project not found';
          } else {
            const roles = userRole.roles;
            let hasInstallationRole = false;
            let hasAdminRole = false;

            for (const role of roles) {
              const roleType = getRoleType(role);
              if (roleType === 'instalation') {
                hasInstallationRole = true;
              } else if (roleType === 'admin') {
                hasAdminRole = true;
              }
            }

            const texts = [];
            if (hasInstallationRole && fullProject.type_swo_instalation) {
              texts.push(fullProject.type_swo_instalation);
            }
            if (hasAdminRole && fullProject.type_swo_admin) {
              texts.push(fullProject.type_swo_admin);
            }
            typeServiceWork = texts.join('\n\n');

            if (!typeServiceWork.trim()) {
              if (hasInstallationRole && !fullProject.type_swo_instalation) {
                skipReason = 'Project missing type_swo_instalation';
              } else if (hasAdminRole && !fullProject.type_swo_admin) {
                skipReason = 'Project missing type_swo_admin';
              } else {
                skipReason = 'No matching type_swo_* for user role';
              }
            }
          }

          // Skip if type_service_work is empty
          if (!typeServiceWork.trim()) {
            skippedItems.push({ userName, projectName, reason: skipReason });
            console.warn(`Skipped CPO for ${userName} / ${projectName} - ${skipReason}`);
            continue;
          }

          const payload = {
            user: item.user_id,
            project: item.project_id,
            type_service_work: typeServiceWork,
            date_start: item.start_date,
            date_end: endDate,
            required_reports: 'Time Sheets, Daily Reports, Quality Documentation of the turbine manufacturer',
            number_of_turbines: fullProject?.number_of_turbines || '',
            turbine_type: fullProject?.model || ''
          };

          const response = await fetch(`${API_BASE_URL}/api/service-work-orders/`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            createdCount++;
          } else {
            errorItems.push({ userName, projectName, reason: 'API error while creating' });
            console.error(`Failed to create CPO for user ${item.user_id}, project ${item.project_id}`);
          }
        }
      } catch (err) {
        const userName = `${item.user_data?.first_name || ''} ${item.user_data?.last_name || ''}`.trim();
        const projectName = item.project_data?.name || `Project ${item.project_id}`;
        errorItems.push({ userName, projectName, reason: err.message });
        console.error('Error processing CPO:', err);
      }
    }

    setBulkCreating(false);
    setSelectedPlanningRows(new Set());

    // Refresh data
    fetchAllCurrentOrders();
    fetchPlanningData();

    // Show result modal
    setBulkResult({
      created: createdCount,
      extended: extendedCount,
      skipped: skippedItems,
      errors: errorItems
    });
  };

  // Delete CPO handler
  const handleDeleteCPO = async (order) => {
    if (!order || order.is_signed) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/service-work-orders/${order.id}/`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setDeleteConfirm(null);
        fetchOrders();
        fetchAllCurrentOrders();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to delete CPO: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting CPO:', err);
      alert('Error deleting CPO');
    } finally {
      setDeleting(false);
    }
  };

  // Handle sending CPO email
  const handleSendEmail = async (order) => {
    if (!order) return;

    setSendingEmail(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/service-work-orders/${order.id}/send-email/`, {
        method: 'POST',
        headers: getHeaders()
      });

      const data = await response.json();

      if (response.ok) {
        setEmailConfirm(null);
        // Refresh orders list to show updated email_sent_at
        fetchOrders();
        alert(data.message || 'Email sent successfully');
      } else {
        alert(`Error sending email: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error sending CPO email:', err);
      alert('Error sending email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Sortable header component
  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;
    return (
      <th
        className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-steel-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="w-3 h-3 text-brand-500" />
            ) : (
              <ArrowDown className="w-3 h-3 text-brand-500" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-steel-300" />
          )}
        </div>
      </th>
    );
  };

  // Planning sortable header component
  const PlanningSortableHeader = ({ field, children }) => {
    const isActive = planningSortField === field;
    return (
      <th
        className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-steel-100 select-none"
        onClick={() => handlePlanningSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive ? (
            planningSortDirection === 'asc' ? (
              <ArrowUp className="w-3 h-3 text-brand-500" />
            ) : (
              <ArrowDown className="w-3 h-3 text-brand-500" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-steel-300" />
          )}
        </div>
      </th>
    );
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) {
        setShowUserDropdown(false);
        setShowProjectDropdown(false);
        setShowCreatedByDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Fetch users, projects, and roles for modal
  const fetchModalData = async () => {
    setModalLoading(true);
    try {
      const [usersRes, projectsRes, globalRolesRes, userGlobalRolesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/users/minimal/`, { headers: getHeaders() }),
        fetch(`${API_BASE_URL}/api/projects/`, { headers: getHeaders() }),
        fetch(`${API_BASE_URL}/api/global-roles/`, { headers: getHeaders() }),
        fetch(`${API_BASE_URL}/api/user-global-roles/`, { headers: getHeaders() })
      ]);

      let fetchedGlobalRoles = [];
      let fetchedUserGlobalRoles = [];

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(Array.isArray(usersData) ? usersData : (usersData.results || []));
      } else {
        console.error('Users fetch failed:', await usersRes.text());
      }

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(Array.isArray(projectsData) ? projectsData : (projectsData.results || []));
      } else {
        console.error('Projects fetch failed:', await projectsRes.text());
      }

      if (globalRolesRes.ok) {
        const globalRolesData = await globalRolesRes.json();
        fetchedGlobalRoles = Array.isArray(globalRolesData) ? globalRolesData : (globalRolesData.results || []);
        setGlobalRoles(fetchedGlobalRoles);
      }

      if (userGlobalRolesRes.ok) {
        const userGlobalRolesData = await userGlobalRolesRes.json();
        fetchedUserGlobalRoles = Array.isArray(userGlobalRolesData) ? userGlobalRolesData : (userGlobalRolesData.results || []);
        setUserGlobalRoles(fetchedUserGlobalRoles);
      }

      // Build userRoles map
      const userRolesMap = {};
      for (const userRole of fetchedUserGlobalRoles) {
        const globalRole = fetchedGlobalRoles.find(r => r.id === userRole.role);
        if (globalRole) {
          const userId = userRole.user || userRole.user_id;

          if (!userRolesMap[userId]) {
            userRolesMap[userId] = {
              roles: [],
              colors: []
            };
          }

          userRolesMap[userId].roles.push(globalRole.name);
          userRolesMap[userId].colors.push(globalRole.color);

          // Keep first role as primary
          if (!userRolesMap[userId].role) {
            userRolesMap[userId].role = globalRole.name;
            userRolesMap[userId].color = globalRole.color;
          }
        }
      }
      setUserRoles(userRolesMap);

    } catch (err) {
      console.error('Error fetching modal data:', err);
    } finally {
      setModalLoading(false);
    }
  };

  // Open modal (for new or edit)
  const openModal = (order = null) => {
    setEditingOrder(order);
    if (order) {
      // Edit mode - populate form with existing data
      setFormData({
        user: order.user || '',
        project: order.project || '',
        type_service_work: order.type_service_work || '',
        date_start: order.date_start || '',
        date_end: order.date_end || '',
        required_reports: order.required_reports || '',
        number_of_turbines: order.number_of_turbines || '',
        turbine_type: order.turbine_type || ''
      });
    } else {
      // New mode - empty form with defaults
      setFormData({
        user: '',
        project: '',
        type_service_work: '',
        date_start: '',
        date_end: '',
        required_reports: 'Time Sheets, Daily Reports, Quality Documentation of the turbine manufacturer',
        number_of_turbines: '',
        turbine_type: ''
      });
    }
    setFormError(null);
    setSwoTypeCheckboxes({ admin: false, instalation: false });
    setShowModal(true);
    fetchModalData();
  };

  // Get selected project data
  const selectedProject = formData.project
    ? projects.find(p => p.id === formData.project)
    : null;

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.user || !formData.project || !formData.type_service_work || !formData.date_start || !formData.date_end) {
      setFormError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const isEdit = editingOrder !== null;
      const url = isEdit
        ? `${API_BASE_URL}/api/service-work-orders/${editingOrder.id}/`
        : `${API_BASE_URL}/api/service-work-orders/`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to ${isEdit ? 'update' : 'create'} order`);
      }

      setShowModal(false);
      setEditingOrder(null);
      fetchOrders(); // Refresh issued list
      // Also refresh planning data if on planning tab
      if (activeTab === 'planning') {
        fetchAllCurrentOrders();
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Fetch filter data on mount
  useEffect(() => {
    fetchFilterData();
  }, []);

  // Fetch orders when filters or page changes
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [signedFilter, selectedUsers, selectedProjects, selectedCreatedBy, searchTerm]);

  // Fetch planning data and current CPOs when switching to planning tab
  useEffect(() => {
    if (activeTab === 'planning') {
      fetchPlanningData();
      fetchAllCurrentOrders();
    }
  }, [activeTab, fetchPlanningData, fetchAllCurrentOrders]);

  // Auto-fill turbine fields and date_end when project is selected and projects are loaded
  useEffect(() => {
    if (formData.project && projects.length > 0) {
      const project = projects.find(p => p.id === parseInt(formData.project) || p.id === formData.project);
      if (project) {
        const updates = {};

        // Auto-fill turbine fields if empty
        if (!formData.number_of_turbines && !formData.turbine_type) {
          updates.number_of_turbines = project.number_of_turbines || '';
          updates.turbine_type = project.model || '';
        }

        // Auto-fill date_end with project.end_date + 90 days (for new CPO from planning tab)
        if (!editingOrder && project.end_date && formData.date_end) {
          const projectEndDate = new Date(project.end_date);
          projectEndDate.setDate(projectEndDate.getDate() + 90);
          const calculatedEndDate = projectEndDate.toISOString().split('T')[0];

          // Only update if current date_end is before the calculated end date
          if (formData.date_end < calculatedEndDate) {
            updates.date_end = calculatedEndDate;
          }
        }

        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({ ...prev, ...updates }));
        }
      }
    }
  }, [formData.project, projects, editingOrder]);

  // Auto-fill type_service_work when modal opens with pre-selected user/project (from planning tab)
  // This handles the case when planning API doesn't return type_swo_* fields
  useEffect(() => {
    if (showModal && !editingOrder && formData.user && formData.project && projects.length > 0 && !formData.type_service_work) {
      const project = projects.find(p => p.id === parseInt(formData.project) || p.id === formData.project);
      const userRole = userRoles[formData.user];

      if (project && userRole?.roles?.length > 0) {
        const roles = userRole.roles;
        let hasInstallationRole = false;
        let hasAdminRole = false;

        for (const role of roles) {
          const roleType = getRoleType(role);
          if (roleType === 'instalation') {
            hasInstallationRole = true;
          } else if (roleType === 'admin') {
            hasAdminRole = true;
          }
        }

        const texts = [];
        if (hasInstallationRole && project.type_swo_instalation) {
          texts.push(project.type_swo_instalation);
        }
        if (hasAdminRole && project.type_swo_admin) {
          texts.push(project.type_swo_admin);
        }

        if (texts.length > 0) {
          setFormData(prev => ({
            ...prev,
            type_service_work: texts.join('\n\n')
          }));
        }
      }
    }
  }, [showModal, editingOrder, formData.user, formData.project, projects, userRoles]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedUsers([]);
    setSelectedProjects([]);
    setSelectedCreatedBy([]);
    setSignedFilter('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedUsers.length > 0 || selectedProjects.length > 0 || selectedCreatedBy.length > 0 || signedFilter || searchTerm;

  // Pagination helpers
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-black text-slate-900">Construction Project Orders</h1>
          <p className="text-xs text-slate-500">Manage construction project orders for your projects</p>
        </div>
        {activeTab === 'issued' && (
          <button
            className="h-9 px-4 flex items-center gap-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-brand-500/20"
            onClick={() => openModal()}
          >
            <Plus className="w-4 h-4" />
            New Order
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-steel-200">
        <button
          onClick={() => setActiveTab('issued')}
          className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
            activeTab === 'issued'
              ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
              : 'bg-white border border-steel-200 text-slate-600 hover:bg-steel-50'
          }`}
        >
          Issued Documents
        </button>
        <button
          onClick={() => setActiveTab('planning')}
          className={`h-9 px-4 rounded-xl text-xs font-bold uppercase tracking-wide transition-all ${
            activeTab === 'planning'
              ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20'
              : 'bg-white border border-steel-200 text-slate-600 hover:bg-steel-50'
          }`}
        >
          Planning
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'issued' && (
        <>
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-brand-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-steel-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* User filter - Multi-select */}
          <div className="relative min-w-[180px] filter-dropdown">
            <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">User</label>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowUserDropdown(!showUserDropdown);
                setShowProjectDropdown(false);
                setShowCreatedByDropdown(false);
              }}
              className={`w-full px-3 py-2 text-left flex justify-between items-center border rounded-xl text-sm cursor-pointer ${
                selectedUsers.length > 0 ? 'bg-brand-50 border-brand-200' : 'bg-white border-steel-200'
              }`}
            >
              <span>{selectedUsers.length === 0 ? 'All Users' : `${selectedUsers.length} selected`}</span>
              <span className="text-xs">▼</span>
            </button>
            {showUserDropdown && (
              <div className="absolute top-full left-0 mt-1 min-w-[280px] bg-white border border-steel-200 rounded-xl shadow-lg z-20 flex flex-col max-h-[350px]">
                <div className="p-2 border-b border-steel-100">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 border border-steel-200 rounded text-sm"
                  />
                </div>
                <div className="overflow-y-auto max-h-[280px]">
                  {filterUsers
                    .filter(u => !userSearchQuery || `${u.first_name} ${u.last_name}`.toLowerCase().includes(userSearchQuery.toLowerCase()))
                    .map(user => (
                      <label
                        key={user.id}
                        className={`flex items-center px-3 py-2 cursor-pointer border-b border-steel-50 hover:bg-steel-50 ${
                          selectedUsers.includes(user.id) ? 'bg-brand-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="mr-2"
                        />
                        <span className="text-sm">{user.first_name} {user.last_name}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Project filter - Multi-select */}
          <div className="relative min-w-[180px] filter-dropdown">
            <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Project</label>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowProjectDropdown(!showProjectDropdown);
                setShowUserDropdown(false);
                setShowCreatedByDropdown(false);
              }}
              className={`w-full px-3 py-2 text-left flex justify-between items-center border rounded-xl text-sm cursor-pointer ${
                selectedProjects.length > 0 ? 'bg-brand-50 border-brand-200' : 'bg-white border-steel-200'
              }`}
            >
              <span>{selectedProjects.length === 0 ? 'All Projects' : `${selectedProjects.length} selected`}</span>
              <span className="text-xs">▼</span>
            </button>
            {showProjectDropdown && (
              <div className="absolute top-full left-0 mt-1 min-w-[280px] bg-white border border-steel-200 rounded-xl shadow-lg z-20 flex flex-col max-h-[350px]">
                <div className="p-2 border-b border-steel-100">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={projectSearchQuery}
                    onChange={(e) => setProjectSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 border border-steel-200 rounded text-sm"
                  />
                </div>
                <div className="overflow-y-auto max-h-[280px]">
                  {filterProjects
                    .filter(p => !projectSearchQuery || p.name.toLowerCase().includes(projectSearchQuery.toLowerCase()))
                    .map(project => (
                      <label
                        key={project.id}
                        className={`flex items-center px-3 py-2 cursor-pointer border-b border-steel-50 hover:bg-steel-50 ${
                          selectedProjects.includes(project.id) ? 'bg-brand-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(project.id)}
                          onChange={() => handleProjectToggle(project.id)}
                          className="mr-2"
                        />
                        <span className="text-sm">{project.name}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Created By filter - Multi-select */}
          <div className="relative min-w-[180px] filter-dropdown">
            <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Created By</label>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCreatedByDropdown(!showCreatedByDropdown);
                setShowUserDropdown(false);
                setShowProjectDropdown(false);
              }}
              className={`w-full px-3 py-2 text-left flex justify-between items-center border rounded-xl text-sm cursor-pointer ${
                selectedCreatedBy.length > 0 ? 'bg-brand-50 border-brand-200' : 'bg-white border-steel-200'
              }`}
            >
              <span>{selectedCreatedBy.length === 0 ? 'All' : `${selectedCreatedBy.length} selected`}</span>
              <span className="text-xs">▼</span>
            </button>
            {showCreatedByDropdown && (
              <div className="absolute top-full left-0 mt-1 min-w-[280px] bg-white border border-steel-200 rounded-xl shadow-lg z-20 flex flex-col max-h-[350px]">
                <div className="p-2 border-b border-steel-100">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={createdBySearchQuery}
                    onChange={(e) => setCreatedBySearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 border border-steel-200 rounded text-sm"
                  />
                </div>
                <div className="overflow-y-auto max-h-[280px]">
                  {filterUsers
                    .filter(u => !createdBySearchQuery || `${u.first_name} ${u.last_name}`.toLowerCase().includes(createdBySearchQuery.toLowerCase()))
                    .map(user => (
                      <label
                        key={user.id}
                        className={`flex items-center px-3 py-2 cursor-pointer border-b border-steel-50 hover:bg-steel-50 ${
                          selectedCreatedBy.includes(user.id) ? 'bg-brand-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCreatedBy.includes(user.id)}
                          onChange={() => handleCreatedByToggle(user.id)}
                          className="mr-2"
                        />
                        <span className="text-sm">{user.first_name} {user.last_name}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Signed filter */}
          <div className="w-36">
            <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Signed</label>
            <select
              value={signedFilter}
              onChange={(e) => setSignedFilter(e.target.value)}
              className={`w-full px-3 py-2 border rounded-xl text-sm ${
                signedFilter ? 'bg-brand-50 border-brand-200' : 'bg-white border-steel-200'
              }`}
            >
              <option value="">All</option>
              <option value="true">Signed</option>
              <option value="false">Not Signed</option>
            </select>
          </div>

          {/* Refresh button */}
          <button
            onClick={fetchOrders}
            className="px-3 py-2 border border-steel-200 rounded-xl hover:bg-steel-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Clear filters button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl border border-brand-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-brand-500 mx-auto mb-4" />
            <p className="text-slate-500">Loading construction project orders...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <XCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchOrders}
              className="mt-4 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600"
            >
              Try Again
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-steel-300 mx-auto mb-4" />
            <p className="text-slate-500">No construction project orders found</p>
            <p className="text-steel-400 text-sm mt-1">
              {hasActiveFilters
                ? 'Try adjusting your filters'
                : 'Create your first order to get started'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-steel-50 border-b border-steel-200">
                <tr>
                  <SortableHeader field="user__last_name">User</SortableHeader>
                  <SortableHeader field="project__name">Project</SortableHeader>
                  <SortableHeader field="project__project_number">Project Number</SortableHeader>
                  <SortableHeader field="date_start">Date Range</SortableHeader>
                  <SortableHeader field="created_by__last_name">Created By</SortableHeader>
                  <SortableHeader field="created_at">Created At</SortableHeader>
                  <SortableHeader field="signed_by">Signed</SortableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-steel-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-steel-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-steel-400 mr-2" />
                        <span className="text-sm text-slate-900">{order.user_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building className="w-4 h-4 text-steel-400 mr-2" />
                        <span className="text-sm text-slate-900">{order.project_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-slate-900">{order.project_number || '-'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center text-sm text-slate-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        {order.date_start} - {order.date_end}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-slate-900">{order.created_by_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-slate-500">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {order.is_signed ? (
                        <div className="text-sm">
                          <span className="text-green-600 flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {order.signed_by_name}
                          </span>
                          <span className="text-xs text-steel-400">
                            {order.signed_at ? new Date(order.signed_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      ) : (
                        <span className="text-steel-400 text-sm">Not signed</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      {order.is_signed ? (
                        order.file ? (
                          <a
                            href={order.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-brand-500 hover:text-brand-600"
                          >
                            View PDF
                          </a>
                        ) : (
                          <span className="text-sm text-steel-400">No file</span>
                        )
                      ) : (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => openModal(order)}
                            className="text-sm font-medium text-brand-500 hover:text-brand-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setEmailConfirm(order)}
                            className={`text-sm font-medium flex items-center ${
                              order.email_sent_at
                                ? 'text-orange-600 hover:text-orange-800'
                                : 'text-green-600 hover:text-green-800'
                            }`}
                            title={order.email_sent_at
                              ? `Sent ${new Date(order.email_sent_at).toLocaleString()} by ${order.email_sent_by_name}`
                              : 'Send email for signing'
                            }
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            {order.email_sent_at ? 'Resend' : 'Send'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(order)}
                            className="text-sm font-medium text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && orders.length > 0 && (
          <div className="px-4 py-3 bg-steel-50 border-t border-steel-200 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} orders
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={!hasPrevPage}
                className="px-3 py-1 border border-steel-200 rounded-xl hover:bg-steel-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Prev
              </button>
              <span className="text-sm text-slate-600 px-3">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={!hasNextPage}
                className="px-3 py-1 border border-steel-200 rounded-xl hover:bg-steel-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Planning Tab Content */}
      {activeTab === 'planning' && (
        <div className="bg-white rounded-xl shadow">
          {planningLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-brand-500 mx-auto mb-4" />
              <p className="text-slate-500">Loading planning data...</p>
            </div>
          ) : planningError ? (
            <div className="p-8 text-center">
              <XCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
              <p className="text-red-600">{planningError}</p>
              <button
                onClick={fetchPlanningData}
                className="mt-4 px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          ) : aggregatedPlanningData.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="w-12 h-12 text-steel-300 mx-auto mb-4" />
              <p className="text-slate-500">No active or upcoming assignments found</p>
              <p className="text-steel-400 text-sm mt-1">Assignments will appear here when scheduled in Resource Planning</p>
            </div>
          ) : (
            <>
              {/* Bulk action toolbar */}
              {selectedPlanningRows.size > 0 && (
                <div className="px-4 py-3 bg-brand-50 border-b border-brand-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-brand-600">
                      {selectedPlanningRows.size} row(s) selected
                    </span>
                    <button
                      onClick={() => setSelectedPlanningRows(new Set())}
                      className="text-sm text-brand-500 hover:text-brand-600 underline"
                    >
                      Clear selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkCreateCPO}
                    disabled={bulkCreating}
                    className="flex items-center px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {bulkCreating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing CPOs...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Process selected ({selectedPlanningRows.size})
                      </>
                    )}
                  </button>
                </div>
              )}
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-steel-50 border-b border-steel-200">
                  <tr>
                    {/* Select all checkbox */}
                    <th className="px-4 py-3 text-left w-12">
                      <input
                        type="checkbox"
                        checked={
                          aggregatedPlanningData.length > 0 &&
                          selectedPlanningRows.size === aggregatedPlanningData.length
                        }
                        onChange={handleSelectAllPlanning}
                        className="w-4 h-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500 cursor-pointer"
                        title="Select all rows for bulk CPO processing"
                      />
                    </th>
                    <PlanningSortableHeader field="user">User</PlanningSortableHeader>
                    <PlanningSortableHeader field="project">Project</PlanningSortableHeader>
                    <PlanningSortableHeader field="period">Missing CPO Period</PlanningSortableHeader>
                    <PlanningSortableHeader field="status">Status</PlanningSortableHeader>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-steel-200">
                  {aggregatedPlanningData.map((item) => (
                    <tr
                      key={`${item.user_id}-${item.project_id}`}
                      className={`hover:bg-steel-50 ${selectedPlanningRows.has(getPlanningRowKey(item)) ? 'bg-brand-50' : ''}`}
                    >
                      {/* Row checkbox */}
                      <td className="px-4 py-3 whitespace-nowrap w-12">
                        <input
                          type="checkbox"
                          checked={selectedPlanningRows.has(getPlanningRowKey(item))}
                          onChange={() => handleSelectPlanningRow(item)}
                          className="w-4 h-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-steel-400 mr-2" />
                          <span className="text-sm text-slate-900">
                            {item.user_data?.first_name} {item.user_data?.last_name}
                            {userRoles[item.user_id]?.roles?.length > 0 && (
                              <span className="text-slate-500 ml-1">
                                [{userRoles[item.user_id].roles.join(', ')}]
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-steel-400 mr-2" />
                          <span className="text-sm text-slate-900">{item.project_data?.name || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center text-sm text-slate-900">
                          <Calendar className="w-4 h-4 mr-1 text-red-400" />
                          {item.start_date} - {item.end_date}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {item.has_partial_coverage ? (
                          item.can_extend_cpo ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Can extend
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Partial (signed)
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            No CPO
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {item.can_extend_cpo ? (
                          <button
                            onClick={() => {
                              // Edit existing CPO - extend its end_date
                              const cpo = item.existing_cpo;
                              setFormData({
                                user: cpo.user,
                                project: cpo.project,
                                type_service_work: cpo.type_service_work || '',
                                date_start: cpo.date_start,
                                date_end: item.end_date, // Extend to cover the gap
                                required_reports: cpo.required_reports || '',
                                number_of_turbines: cpo.number_of_turbines || '',
                                turbine_type: cpo.turbine_type || ''
                              });
                              setEditingOrder(cpo);
                              setSwoTypeCheckboxes({ admin: false, instalation: false });
                              setFormError(null);
                              fetchModalData();
                              setShowModal(true);
                            }}
                            className="text-sm font-medium text-yellow-600 hover:text-yellow-800"
                          >
                            Extend CPO
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              // Pre-fill form with this data and open modal
                              // Calculate type_service_work based on user role
                              const userId = item.user_id;
                              const userRole = userRoles[userId];
                              let typeServiceWork = '';

                              if (userRole?.roles?.length > 0 && item.project_data) {
                                const roles = userRole.roles;
                                let hasInstallationRole = false;
                                let hasAdminRole = false;

                                for (const role of roles) {
                                  const roleType = getRoleType(role);
                                  if (roleType === 'instalation') {
                                    hasInstallationRole = true;
                                  } else if (roleType === 'admin') {
                                    hasAdminRole = true;
                                  }
                                }

                                const texts = [];
                                if (hasInstallationRole && item.project_data.type_swo_instalation) {
                                  texts.push(item.project_data.type_swo_instalation);
                                }
                                if (hasAdminRole && item.project_data.type_swo_admin) {
                                  texts.push(item.project_data.type_swo_admin);
                                }
                                typeServiceWork = texts.join('\n\n');
                              }

                              // Calculate end date: project end_date + 90 days
                              let endDate = item.end_date;
                              if (item.project_data?.end_date) {
                                const projectEndDate = new Date(item.project_data.end_date);
                                projectEndDate.setDate(projectEndDate.getDate() + 90);
                                endDate = projectEndDate.toISOString().split('T')[0];
                              }

                              setFormData({
                                user: item.user_id,
                                project: item.project_id,
                                type_service_work: typeServiceWork,
                                date_start: item.start_date,
                                date_end: endDate,
                                required_reports: 'Time Sheets, Daily Reports, Quality Documentation of the turbine manufacturer',
                                number_of_turbines: item.project_data?.number_of_turbines || '',
                                turbine_type: item.project_data?.model || ''
                              });
                              setEditingOrder(null);
                              setSwoTypeCheckboxes({ admin: false, instalation: false });
                              setFormError(null);
                              fetchModalData();
                              setShowModal(true);
                            }}
                            className="text-sm font-medium text-brand-500 hover:text-brand-600"
                          >
                            Create CPO
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div
            className={`bg-white rounded-xl shadow-xl flex flex-col transition-all duration-300 ${
              formData.project ? 'w-full max-w-5xl' : 'w-full max-w-lg'
            }`}
            style={{ height: '90vh' }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-steel-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                {editingOrder ? (
                  <Edit className="w-5 h-5 mr-2 text-brand-500" />
                ) : (
                  <Plus className="w-5 h-5 mr-2 text-brand-500" />
                )}
                {editingOrder ? 'Edit Construction Project Order' : 'New Construction Project Order'}
              </h2>
              <button
                onClick={() => { setShowModal(false); setEditingOrder(null); }}
                className="text-steel-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex flex-1 overflow-hidden">
                {/* Left Panel - Form */}
                <div className={`flex flex-col ${formData.project ? 'w-1/2 border-r border-steel-200' : 'w-full'}`}>
                  <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                    {formError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                        {formError}
                      </div>
                    )}

                    {modalLoading ? (
                      <div className="py-8 text-center">
                        <RefreshCw className="w-6 h-6 animate-spin text-brand-500 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">Loading data...</p>
                      </div>
                    ) : (
                      <>
                        {/* User */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            User <span className="text-red-500">*</span>
                          </label>
                          <Select
                            options={userOptions}
                            value={userOptions.find(option => option.value === formData.user) || null}
                            onChange={(selected) => {
                              const newUserId = selected ? selected.value : '';
                              const updatedFormData = updateTypeServiceWork(newUserId, formData.project, {
                                ...formData,
                                user: newUserId
                              });
                              setFormData(updatedFormData);
                            }}
                            placeholder="Search and select user..."
                            isClearable
                            isSearchable
                            styles={selectStyles}
                            noOptionsMessage={() => "No users found"}
                          />
                        </div>

                        {/* Project */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Project <span className="text-red-500">*</span>
                          </label>
                          <Select
                            options={projectOptions}
                            value={projectOptions.find(option => option.value === formData.project) || null}
                            onChange={(selected) => {
                              if (selected) {
                                const project = projects.find(p => p.id === selected.value);

                                // Calculate dates: start = today, end = project end_date + 90 days
                                const today = new Date().toISOString().split('T')[0];
                                let endDate = '';
                                if (project?.end_date) {
                                  const projectEndDate = new Date(project.end_date);
                                  projectEndDate.setDate(projectEndDate.getDate() + 90);
                                  endDate = projectEndDate.toISOString().split('T')[0];
                                }

                                const baseFormData = {
                                  ...formData,
                                  project: selected.value,
                                  number_of_turbines: project?.number_of_turbines || '',
                                  turbine_type: project?.model || '',
                                  date_start: formData.date_start || today,
                                  date_end: endDate || formData.date_end
                                };

                                // Auto-fill type_service_work based on user role
                                const updatedFormData = updateTypeServiceWork(formData.user, selected.value, baseFormData);
                                setFormData(updatedFormData);
                              } else {
                                setFormData({
                                  ...formData,
                                  project: '',
                                  number_of_turbines: '',
                                  turbine_type: '',
                                  date_start: '',
                                  date_end: '',
                                  type_service_work: ''
                                });
                                setSwoTypeCheckboxes({ admin: false, instalation: false });
                              }
                            }}
                            placeholder="Search and select project..."
                            isClearable
                            isSearchable
                            styles={selectStyles}
                            noOptionsMessage={() => "No projects found"}
                          />
                        </div>

                        {/* Type of Service Work */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Type of Service Work <span className="text-red-500">*</span>
                          </label>

                          {/* Warning and checkboxes for unknown role */}
                          {formData.user && formData.project && !userRoles[formData.user]?.roles?.length && (
                            <div className="mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                              <div className="flex items-center text-yellow-800 text-sm font-medium mb-2">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Unknown role - select type manually:
                              </div>
                              <div className="flex gap-4">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={swoTypeCheckboxes.admin}
                                    onChange={(e) => {
                                      const newCheckboxes = { ...swoTypeCheckboxes, admin: e.target.checked };
                                      setSwoTypeCheckboxes(newCheckboxes);
                                      const project = projects.find(p => p.id === formData.project);
                                      const texts = [];
                                      if (newCheckboxes.admin && project?.type_swo_admin) texts.push(project.type_swo_admin);
                                      if (newCheckboxes.instalation && project?.type_swo_instalation) texts.push(project.type_swo_instalation);
                                      setFormData({ ...formData, type_service_work: texts.join('\n\n') });
                                    }}
                                    className="w-4 h-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
                                  />
                                  <span className="ml-2 text-sm text-slate-700">Administration</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={swoTypeCheckboxes.instalation}
                                    onChange={(e) => {
                                      const newCheckboxes = { ...swoTypeCheckboxes, instalation: e.target.checked };
                                      setSwoTypeCheckboxes(newCheckboxes);
                                      const project = projects.find(p => p.id === formData.project);
                                      const texts = [];
                                      if (newCheckboxes.admin && project?.type_swo_admin) texts.push(project.type_swo_admin);
                                      if (newCheckboxes.instalation && project?.type_swo_instalation) texts.push(project.type_swo_instalation);
                                      setFormData({ ...formData, type_service_work: texts.join('\n\n') });
                                    }}
                                    className="w-4 h-4 text-brand-500 border-steel-300 rounded focus:ring-brand-500"
                                  />
                                  <span className="ml-2 text-sm text-slate-700">Installation</span>
                                </label>
                              </div>
                            </div>
                          )}

                          <textarea
                            value={formData.type_service_work}
                            onChange={(e) => setFormData({ ...formData, type_service_work: e.target.value })}
                            className="w-full px-3 py-2 border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                            rows={5}
                            placeholder="e.g., Maintenance, Inspection, Repair..."
                            required
                          />
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={formData.date_start}
                              onChange={(e) => setFormData({ ...formData, date_start: e.target.value })}
                              className="w-full px-3 py-2 border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              End Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={formData.date_end}
                              onChange={(e) => setFormData({ ...formData, date_end: e.target.value })}
                              className="w-full px-3 py-2 border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                              required
                            />
                          </div>
                        </div>

                        {/* Required Reports */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Required Reports
                          </label>
                          <textarea
                            value={formData.required_reports}
                            onChange={(e) => setFormData({ ...formData, required_reports: e.target.value })}
                            className="w-full px-3 py-2 border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                            rows={3}
                            placeholder="List required reports..."
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Right Panel - Project Details (shown when project is selected) */}
                {formData.project && selectedProject && (
                  <div className="w-1/2 flex flex-col bg-steel-50">
                    <div className="px-6 py-4 border-b border-steel-200">
                      <h3 className="text-md font-semibold text-slate-900 flex items-center">
                        <Building className="w-4 h-4 mr-2 text-brand-500" />
                        Project Details
                      </h3>
                    </div>
                    <div className="px-6 py-4 overflow-y-auto flex-1 space-y-5">
                      {/* Project Info - Read Only */}
                      <div className="space-y-4">
                        {/* Project Name & Address */}
                        <div className="bg-white rounded-xl p-4 border border-steel-200">
                          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                            Project Name & Address
                          </label>
                          <p className="text-sm font-semibold text-slate-900">{selectedProject.name}</p>
                          {(selectedProject.street_address || selectedProject.city || selectedProject.postal_code || selectedProject.country) && (
                            <p className="text-sm text-slate-600 mt-1">
                              {[
                                selectedProject.street_address,
                                selectedProject.city,
                                selectedProject.postal_code,
                                selectedProject.country
                              ].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>

                        {/* Project Number */}
                        <div className="bg-white rounded-xl p-4 border border-steel-200">
                          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                            Project Number
                          </label>
                          <p className="text-sm font-semibold text-slate-900">
                            {selectedProject.project_number || <span className="text-steel-400 font-normal">Not specified</span>}
                          </p>
                        </div>

                        {/* Customer */}
                        <div className="bg-white rounded-xl p-4 border border-steel-200">
                          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                            Customer
                          </label>
                          <p className="text-sm font-semibold text-slate-900">
                            {selectedProject.customer || <span className="text-steel-400 font-normal">Not specified</span>}
                          </p>
                        </div>
                      </div>

                      {/* Editable Fields */}
                      <div className="border-t border-steel-200 pt-5">
                        <h4 className="text-sm font-medium text-slate-700 mb-4">Construction Project Order Details</h4>

                        {/* Number of Turbines */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Number of Turbines
                          </label>
                          <input
                            type="text"
                            value={formData.number_of_turbines}
                            onChange={(e) => setFormData({ ...formData, number_of_turbines: e.target.value })}
                            className="w-full px-3 py-2 border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
                            placeholder="Enter number of turbines..."
                          />
                          {selectedProject.number_of_turbines && formData.number_of_turbines !== selectedProject.number_of_turbines && (
                            <p className="text-xs text-steel-400 mt-1">Project default: {selectedProject.number_of_turbines}</p>
                          )}
                        </div>

                        {/* Turbine Type */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Turbine Type
                          </label>
                          <input
                            type="text"
                            value={formData.turbine_type}
                            onChange={(e) => setFormData({ ...formData, turbine_type: e.target.value })}
                            className="w-full px-3 py-2 border border-steel-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
                            placeholder="Enter turbine type..."
                          />
                          {selectedProject.model && formData.turbine_type !== selectedProject.model && (
                            <p className="text-xs text-steel-400 mt-1">Project default: {selectedProject.model}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-steel-200 bg-steel-50 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingOrder(null); }}
                  className="px-4 py-2 text-slate-700 border border-steel-300 rounded-xl hover:bg-steel-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || modalLoading}
                  className="flex items-center px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingOrder ? 'Update Order' : 'Create Order'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Processing Result Modal */}
      {bulkResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-steel-200">
              <h2 className="text-lg font-semibold text-slate-900">
                CPO Processing Results
              </h2>
              <button
                onClick={() => setBulkResult(null)}
                className="text-steel-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {/* Success summary */}
              <div className="flex gap-4 mb-4">
                {bulkResult.created > 0 && (
                  <div className="flex items-center px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-green-800 font-medium">{bulkResult.created} created</span>
                  </div>
                )}
                {bulkResult.extended > 0 && (
                  <div className="flex items-center px-3 py-2 bg-brand-50 border border-brand-200 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-brand-500 mr-2" />
                    <span className="text-brand-600 font-medium">{bulkResult.extended} extended</span>
                  </div>
                )}
              </div>

              {/* Skipped items */}
              {bulkResult.skipped.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
                    <XCircle className="w-4 h-4 mr-1" />
                    Skipped ({bulkResult.skipped.length})
                  </h3>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl divide-y divide-yellow-200">
                    {bulkResult.skipped.map((item, idx) => (
                      <div key={idx} className="px-3 py-2">
                        <div className="text-sm font-medium text-slate-900">
                          {item.userName} - {item.projectName}
                        </div>
                        <div className="text-xs text-yellow-700">{item.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error items */}
              {bulkResult.errors.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-800 mb-2 flex items-center">
                    <XCircle className="w-4 h-4 mr-1" />
                    Errors ({bulkResult.errors.length})
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-xl divide-y divide-red-200">
                    {bulkResult.errors.map((item, idx) => (
                      <div key={idx} className="px-3 py-2">
                        <div className="text-sm font-medium text-slate-900">
                          {item.userName} - {item.projectName}
                        </div>
                        <div className="text-xs text-red-700">{item.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All success message */}
              {bulkResult.skipped.length === 0 && bulkResult.errors.length === 0 && (
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-slate-600">All CPOs processed successfully!</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-steel-200 bg-steel-50">
              <button
                onClick={() => setBulkResult(null)}
                className="w-full px-4 py-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-steel-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Delete CPO
              </h2>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="text-steel-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-slate-700 mb-4">
                Are you sure you want to delete this CPO?
              </p>
              <div className="bg-steel-50 rounded-xl p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-steel-400" />
                  <span className="font-medium">{deleteConfirm.user_name}</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Building className="w-4 h-4 text-steel-400" />
                  <span>{deleteConfirm.project_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-steel-400" />
                  <span>{deleteConfirm.date_start} - {deleteConfirm.date_end}</span>
                </div>
              </div>
              <p className="text-red-600 text-sm mt-4">
                This action cannot be undone.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-steel-200 bg-steel-50 flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 text-slate-700 border border-steel-300 rounded-xl hover:bg-steel-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCPO(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Confirmation Modal */}
      {emailConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-steel-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-green-600" />
                Send CPO for Signing
              </h2>
              <button
                onClick={() => setEmailConfirm(null)}
                className="text-steel-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              {/* Warning if already sent */}
              {emailConfirm.email_sent_at && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4">
                  <p className="text-orange-800 text-sm font-medium">
                    Email was already sent
                  </p>
                  <p className="text-orange-600 text-xs mt-1">
                    {new Date(emailConfirm.email_sent_at).toLocaleString()} by {emailConfirm.email_sent_by_name}
                  </p>
                </div>
              )}

              <p className="text-slate-700 mb-4">
                {emailConfirm.email_sent_at
                  ? 'Do you want to resend this CPO via email?'
                  : 'Do you want to send this CPO via email for signing?'
                }
              </p>
              <div className="bg-steel-50 rounded-xl p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-steel-400" />
                  <span className="font-medium">{emailConfirm.user_name}</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-steel-400" />
                  <span className="text-brand-500">{emailConfirm.user_email || 'No email'}</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Building className="w-4 h-4 text-steel-400" />
                  <span>{emailConfirm.project_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-steel-400" />
                  <span>{emailConfirm.date_start} - {emailConfirm.date_end}</span>
                </div>
              </div>
              <p className="text-slate-500 text-sm mt-4">
                The email with attached PDF will be sent to: <strong className="text-slate-700">{emailConfirm.user_email || 'N/A'}</strong>
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-steel-200 bg-steel-50 flex gap-3 justify-end">
              <button
                onClick={() => setEmailConfirm(null)}
                disabled={sendingEmail}
                className="px-4 py-2 text-slate-700 border border-steel-300 rounded-xl hover:bg-steel-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSendEmail(emailConfirm)}
                disabled={sendingEmail}
                className={`px-4 py-2 text-white rounded-xl transition-colors disabled:opacity-50 flex items-center ${
                  emailConfirm.email_sent_at
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {sendingEmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    {emailConfirm.email_sent_at ? 'Resend Email' : 'Send Email'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceWorkOrder;
